import React from 'react';

import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { CreateEstimateModal } from '@features';
import { Flex, Icon, Skeleton, UiButton, UiText } from '@shared/ui';
import { exportEstimateInformation } from '@widgets/EstimateInformation/api/exportEstimateInformation';
import { EstimateInformationContext } from '@widgets/EstimateInformation/model/context';
import { EstimateMainInfo } from '@widgets/EstimateInformation/ui/EstimateMainInfo';
import { EstimateParticipants } from '@widgets/EstimateInformation/ui/EstimateParticipants';
import { EstimateSettings } from '@widgets/EstimateInformation/ui/EstimateSettings';
import { AddEmployeesModal } from '@widgets/EstimateInformation/ui/modals/AddUsersModal/AddEmployeesModal';
import { GanttModal } from '@widgets/EstimateInformation/ui/modals/GanttModal';

import cls from './EstimateInformation.module.css';
import { useEstimateInformation } from '../lib/hooks/useEstimateInformation';

interface EstimateInformationProps {
    classes?: string;
}

export const EstimateInformation = ({ classes }: EstimateInformationProps) => {
    const {
        context,
        onCloseCreateEstimateModal,
        isOpenCreateEstimateModal,
        data,
        onHandleAddEmployeesModal,
        isOpenAddEmployeesModal,
        isOpenGanttModal,
        onCloseGanttModal,
        isPending,
    } = useEstimateInformation();

    const { t } = useTranslation();

    return (
        <EstimateInformationContext.Provider value={context}>
            <div className={cn(cls.EstimateInformation, classes)}>
                <Flex justify="between" align="center" gap={36}>
                    {isPending ? (
                        <Skeleton width={357} height={24} border="8px" />
                    ) : (
                        <UiText tag="h1" variant="heading" weight="bold">
                            {data?.title}
                        </UiText>
                    )}
                    <UiButton
                        onClick={exportEstimateInformation}
                        className={cls.exportButton}
                        btnText={t(
                            'EstimateInformation.return.EstimateInformationContext.EstimateInformation.UITooltip.label'
                        )}
                        startIcon={<Icon fill="currentColor" iconName="export" />}
                    />
                </Flex>
                <Flex direction="column" align="start" gap={8}>
                    <EstimateMainInfo />
                    <EstimateParticipants onToggleModal={onHandleAddEmployeesModal} />
                    <EstimateSettings />
                </Flex>
            </div>
            {isOpenCreateEstimateModal && (
                <CreateEstimateModal
                    handleClose={onCloseCreateEstimateModal}
                    isOpen={isOpenCreateEstimateModal}
                />
            )}
            {isOpenAddEmployeesModal && (
                <AddEmployeesModal
                    mode={isOpenAddEmployeesModal}
                    onToggleModal={onHandleAddEmployeesModal}
                />
            )}
            {isOpenGanttModal && (
                <GanttModal isOpen={isOpenGanttModal} handleClose={onCloseGanttModal} />
            )}
        </EstimateInformationContext.Provider>
    );
};
